import { useState } from "react";
import { Check, ChevronRight, X } from "lucide-react";
import { ApiError, unansweredInSection, type AnswerMap, type InstrumentName, type ItemMap } from "@ferrivia/api-contract";
import { Button, Screen, Text } from "@ferrivia/ui";
import { useQuestionnaireCopy, SYMPTOM_STEP_ID } from "@ferrivia/questionnaire-ui";
import { useTranslation } from "@ferrivia/i18n";
import type { SectionProgress } from "@ferrivia/api-contract";
import s from "./HubScreen.module.css";

export interface HubScreenProps {
  instrument: InstrumentName;
  sections: SectionProgress[];
  firstUnfinishedSectionId: string | null;
  completeSectionIds: ReadonlySet<string>;
  answers: AnswerMap;
  items: ItemMap;
  symptomCheckDone: boolean;
  /** Also opens the symptom step, under `SYMPTOM_STEP_ID`. */
  onOpenSection(sectionId: string): void;
  onSubmit(): Promise<void>;
  onClose(): void;
}

type SubmitStatus = "idle" | "submitting" | "empty" | "symptomCheckMissing" | "failed";

/** The section map a client returning to a paused questionnaire lands on --
    a first-time client with no saved answers goes straight into section 1
    instead (see `QuestionnaireOverlay`'s landing effect). Deliberately no
    question counts anywhere on this screen -- a complete row turns brand-tinted
    with a solid check badge and a visible "Valmis", an unfinished one stays
    neutral, and the overall bar underneath the title is a fraction with no
    figure, so a follow-up question revealing itself never reads as a number
    going backwards. "Valmis" is a form-progress fact, never a judgement about
    an answer, so it uses brand tokens and never --color-success.

    Submitting happens from here too. There used to be a review screen between
    this one and the send, listing every reachable question as
    answered/unanswered; it was cut (Ville, 2026-09-14) as a restatement of
    the per-section counts already on this screen -- it showed no answer, so
    there was nothing on it to actually review. */
export function HubScreen({
  instrument,
  sections,
  firstUnfinishedSectionId,
  completeSectionIds,
  answers,
  items,
  symptomCheckDone,
  onOpenSection,
  onSubmit,
  onClose,
}: HubScreenProps) {
  const { t } = useTranslation("client");
  const copy = useQuestionnaireCopy();
  const [status, setStatus] = useState<SubmitStatus>("idle");

  const totalReachable = sections.reduce((sum, section) => sum + section.reachable, 0);
  const totalAnswered = sections.reduce((sum, section) => sum + section.answered, 0);
  const overallFraction = totalReachable === 0 ? 0 : totalAnswered / totalReachable;

  // The symptom step is the one required part: the server 400s a submit
  // without it (symptom_check_missing).
  const nextStepId = firstUnfinishedSectionId ?? (symptomCheckDone ? null : SYMPTOM_STEP_ID);
  const readyToSubmit = nextStepId === null;
  // The only disabling gate on Lähetä, inherited from the review screen:
  // the server 400s an all-empty submission (empty_submission), so a send
  // with nothing at all answered is headed off before the round trip.
  // Partial submissions stay allowed -- this is not "every question".
  const nothingAnswered = totalReachable > 0 && totalAnswered === 0;

  const primaryLabel =
    nextStepId !== null
      ? t("questionnaire.hub.continueCta", "Jatka kohdasta {{section}}", {
          section: copy.sectionFor(nextStepId)?.title ?? "",
        })
      : status === "submitting"
        ? t("questionnaire.hub.submitting", "Lähetetään…")
        : t("questionnaire.hub.submitCta", "Lähetä");

  async function handleSubmit() {
    if (nothingAnswered || status === "submitting") return;
    setStatus("submitting");
    try {
      await onSubmit();
    } catch (err) {
      if (err instanceof ApiError && err.code === "empty_submission") setStatus("empty");
      else if (err instanceof ApiError && err.code === "symptom_check_missing") setStatus("symptomCheckMissing");
      else setStatus("failed");
    }
  }

  function handlePrimaryClick() {
    if (nextStepId !== null) onOpenSection(nextStepId);
    else void handleSubmit();
  }

  return (
    <Screen className={s.root}>
      {/* Title and close share a row: the close button alone above the
          heading left a band of empty screen doing nothing. */}
      <div className={s.header}>
        <Text as="h1" size="2xl" weight="bold" className={s.title}>
          {t("questionnaire.hub.title", "Esitiedot")}
        </Text>
        <button type="button" className={s.close} onClick={onClose} aria-label={t("questionnaire.close", "Sulje")}>
          <X aria-hidden size={20} />
        </button>
      </div>

      <div className={s.scroll}>
        <div className={s.overallBar} role="presentation">
          <div className={s.overallBarFill} style={{ width: `${Math.round(overallFraction * 100)}%` }} />
        </div>

        <ul className={s.list}>
          {sections.map((section) => {
            const title = copy.sectionFor(section.sectionId)?.title ?? section.sectionId;
            // A section with nothing that counts toward completion
            // (additional_information) is complete once visited -- leaving
            // it blank is an answer. See useQuestionnaire's completeSectionIds.
            const complete = completeSectionIds.has(section.sectionId);
            // Only once the client has started a section: an untouched one
            // has nothing "missing" yet, it simply hasn't been opened.
            const missing =
              !complete && section.answered > 0
                ? unansweredInSection(instrument, section.sectionId, answers, items)
                : [];
            const missingText =
              missing.length === 1
                ? t("questionnaire.hub.missingOne", "Vastaamatta: {{question}}", {
                    question: copy.questionFor(missing[0])?.label ?? missing[0],
                  })
                : missing.length > 1
                  ? t("questionnaire.hub.missingMany", "{{count}} kysymystä vastaamatta", { count: missing.length })
                  : null;
            return (
              <li key={section.sectionId}>
                <button
                  type="button"
                  className={complete ? `${s.row} ${s.rowDone}` : s.row}
                  onClick={() => onOpenSection(section.sectionId)}
                >
                  <span className={s.rowText}>
                    <span className={s.rowTitle}>{title}</span>
                    {missingText ? <span className={s.rowMissing}>{missingText}</span> : null}
                  </span>
                  <span className={s.rowMeta}>
                    {complete ? (
                      <>
                        <span className={s.rowBadge} aria-hidden>
                          <Check size={14} strokeWidth={3} />
                        </span>
                        <span className={s.rowDoneLabel}>{t("questionnaire.hub.sectionComplete", "Valmis")}</span>
                      </>
                    ) : null}
                    <ChevronRight aria-hidden size={18} className={s.rowChevron} />
                  </span>
                </button>
              </li>
            );
          })}
          <li>
            <button
              type="button"
              className={symptomCheckDone ? `${s.row} ${s.rowDone}` : s.row}
              onClick={() => onOpenSection(SYMPTOM_STEP_ID)}
            >
              <span className={s.rowTitle}>{copy.sectionFor(SYMPTOM_STEP_ID)?.title ?? SYMPTOM_STEP_ID}</span>
              <span className={s.rowMeta}>
                {symptomCheckDone ? (
                  <>
                    <span className={s.rowBadge} aria-hidden>
                      <Check size={14} strokeWidth={3} />
                    </span>
                    <span className={s.rowDoneLabel}>{t("questionnaire.hub.sectionComplete", "Valmis")}</span>
                  </>
                ) : null}
                <ChevronRight aria-hidden size={18} className={s.rowChevron} />
              </span>
            </button>
          </li>
        </ul>
      </div>

      <div className={s.footer}>
        {status === "empty" ? (
          <Text as="p" size="sm" className={s.error} role="alert">
            {t("questionnaire.hub.emptySubmission", "Vastaa ainakin yhteen kysymykseen ennen lähettämistä.")}
          </Text>
        ) : null}
        {status === "symptomCheckMissing" ? (
          <Text as="p" size="sm" className={s.error} role="alert">
            {t("questionnaire.hub.symptomCheckMissing", "Täytä vielä oirekysely.")}
          </Text>
        ) : null}
        {status === "failed" ? (
          <Text as="p" size="sm" className={s.error} role="alert">
            {t("questionnaire.hub.submitFailed", "Lähettäminen epäonnistui. Yritä uudelleen.")}
          </Text>
        ) : null}
        <Button
          className={s.primary}
          disabled={readyToSubmit && (nothingAnswered || status === "submitting")}
          onClick={handlePrimaryClick}
        >
          {primaryLabel}
        </Button>
      </div>
    </Screen>
  );
}
